/**
 * AIProductImageGenerator - Composant de génération d'images produits par IA
 * 
 * Permet de générer des images pour un ou plusieurs produits avec :
 * - Choix du style visuel OU atmosphère personnalisée
 * - Prompt personnalisé libre
 * - Choix du format (1:1, 4:5, 16:9)
 * - Nombre de variantes
 * - Option pour définir comme image principale
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Sparkles, X, Image, Wand2, Check, AlertCircle, 
  Loader2, Square, RectangleVertical, RectangleHorizontal,
  Mic, Globe, MapPin
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { VoiceInput } from '../ui/VoiceInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Styles visuels disponibles (fallback si pas d'atmosphères)
const VISUAL_STYLES = [
  { id: 'editorial', name: 'Editorial', desc: 'Haute couture, studio pro', icon: '📸' },
  { id: 'cinematic', name: 'Cinématique', desc: 'Style film, dramatique', icon: '🎬' },
  { id: 'minimalist', name: 'Minimaliste', desc: 'Fond simple, épuré', icon: '⬜' },
  { id: 'urban', name: 'Urbain', desc: 'Rue, streetwear', icon: '🏙️' },
  { id: 'luxury', name: 'Luxe', desc: 'Élégant, premium', icon: '✨' },
  { id: 'dramatic', name: 'Dramatique', desc: 'Contrastes forts', icon: '🎭' },
];

// Icônes pour les atmosphères
const ATMOSPHERE_ICONS = {
  'Tokyo Street Style': '🗼',
  'Parisian Luxury': '🗼',
  'New York Urban': '🗽',
  'Mediterranean Summer': '🏖️',
  'Studio Minimaliste': '⬜',
  'Dubai Luxury': '🌴',
};

// Formats disponibles
const ASPECT_RATIOS = [
  { id: '1:1', name: 'Carré', desc: 'Fiches produits', icon: Square },
  { id: '4:5', name: 'Portrait', desc: 'Instagram', icon: RectangleVertical },
  { id: '16:9', name: 'Paysage', desc: 'Bannières', icon: RectangleHorizontal },
];

// Helper pour l'URL des images
const getImageUrl = (imagePath) => {
  if (!imagePath) return '/placeholder.png';
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/api/')) return `${API_URL}${imagePath}`;
  return `${API_URL}/api/uploads/${imagePath}`;
};

export function AIProductImageGenerator({ 
  open, 
  onOpenChange, 
  selectedProducts = [], 
  token,
  onComplete 
}) {
  // États
  const [useAtmosphere, setUseAtmosphere] = useState(true); // Par défaut: utiliser atmosphères
  const [atmospheres, setAtmospheres] = useState([]);
  const [selectedAtmosphereId, setSelectedAtmosphereId] = useState(null);
  const [loadingAtmospheres, setLoadingAtmospheres] = useState(false);
  const [visualStyle, setVisualStyle] = useState('editorial');
  const [customPrompt, setCustomPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [variantsCount, setVariantsCount] = useState(1);
  const [setAsMain, setSetAsMain] = useState(true);
  
  // États de génération
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentProduct, setCurrentProduct] = useState('');
  const [results, setResults] = useState(null);

  // Charger les atmosphères au montage
  useEffect(() => {
    if (open && token) {
      fetchAtmospheres();
    }
  }, [open, token]);

  const fetchAtmospheres = async () => {
    setLoadingAtmospheres(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/atmospheres`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAtmospheres(data.atmospheres || []);
        // Sélectionner l'atmosphère par défaut
        if (data.default_id) {
          setSelectedAtmosphereId(data.default_id);
        } else if (data.atmospheres?.length > 0) {
          setSelectedAtmosphereId(data.atmospheres[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching atmospheres:', err);
    } finally {
      setLoadingAtmospheres(false);
    }
  };

  // Estimation du temps
  const estimatedTime = selectedProducts.length * variantsCount * 20; // ~20s par image (marge de sécurité)

  // Lancer la génération
  const handleGenerate = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Aucun produit sélectionné');
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setResults(null);

    const totalImages = selectedProducts.length * variantsCount;
    const estimatedMs = totalImages * 20000; // 20 secondes par image
    
    // Afficher le temps estimé
    const minutes = Math.ceil(estimatedMs / 60000);
    toast.info(`Génération de ${totalImages} image(s) en cours. Temps estimé: ~${minutes} min`, {
      duration: 5000
    });

    // Progression simulée basée sur le temps estimé
    let progressInterval;
    let currentProgress = 0;
    const startTime = Date.now();
    
    progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const expectedProgress = Math.min((elapsed / estimatedMs) * 90, 90); // Max 90% pendant l'attente
      
      // Progression fluide
      if (currentProgress < expectedProgress) {
        currentProgress = Math.min(currentProgress + 2, expectedProgress);
        setProgress(Math.round(currentProgress));
      }
    }, 500);

    // AbortController pour timeout (5 minutes max)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes

    try {
      const response = await fetch(`${API_URL}/api/admin/ai/generate-product-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: selectedProducts.map(p => p._id || p.id),
          visual_style: useAtmosphere ? null : visualStyle,
          atmosphere_id: useAtmosphere ? selectedAtmosphereId : null,
          custom_prompt: customPrompt || null,
          aspect_ratio: aspectRatio,
          variants_count: variantsCount,
          set_as_main: setAsMain
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la génération');
      }

      const data = await response.json();
      setResults(data);
      setProgress(100);

      if (data.success) {
        const successCount = data.summary.products_successful;
        const totalCount = data.summary.products_processed;
        const imgCount = data.summary.total_images_generated;
        
        if (successCount === totalCount) {
          toast.success(`✅ ${imgCount} image(s) générée(s) pour ${successCount} produit(s)`);
        } else {
          toast.warning(`⚠️ ${imgCount} image(s) générée(s). ${successCount}/${totalCount} produits réussis.`);
        }
        
        if (onComplete) {
          onComplete(data);
        }
      }

    } catch (error) {
      clearTimeout(timeoutId);
      clearInterval(progressInterval);
      console.error('Generation error:', error);
      
      if (error.name === 'AbortError') {
        toast.error('Timeout: la génération a pris trop de temps. Réessayez avec moins de produits.');
      } else {
        toast.error(`Erreur: ${error.message || 'Problème lors de la génération'}`);
      }
      setProgress(0);
    } finally {
      setIsGenerating(false);
    }
  };

  // Réinitialiser
  const handleReset = () => {
    setResults(null);
    setProgress(0);
    setCustomPrompt('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            Générateur IA de Visuels Produits
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Générez des images professionnelles pour vos produits avec l'IA
          </DialogDescription>
        </DialogHeader>

        {!results ? (
          <div className="space-y-6 py-4">
            {/* Produits sélectionnés */}
            <div className="space-y-3">
              <Label className="text-white flex items-center gap-2">
                <Image className="w-4 h-4 text-blue-400" />
                Produits sélectionnés ({selectedProducts.length})
              </Label>
              
              {selectedProducts.length === 0 ? (
                <div className="p-4 bg-zinc-800 rounded-lg text-center text-gray-400">
                  Aucun produit sélectionné
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 p-3 bg-zinc-800 rounded-lg max-h-32 overflow-y-auto">
                  {selectedProducts.slice(0, 20).map((product, idx) => (
                    <div 
                      key={product._id || idx}
                      className="flex items-center gap-2 bg-zinc-700 rounded px-2 py-1"
                    >
                      <img 
                        src={getImageUrl(product.images?.main?.[0] || product.images?.gallery?.[0])}
                        alt={product.name?.fr || 'Produit'}
                        className="w-8 h-8 object-cover rounded"
                        onError={(e) => e.target.src = '/placeholder.png'}
                      />
                      <span className="text-xs text-white truncate max-w-[100px]">
                        {product.name?.fr || product.name?.en || 'Produit'}
                      </span>
                    </div>
                  ))}
                  {selectedProducts.length > 20 && (
                    <Badge variant="outline" className="text-gray-400">
                      +{selectedProducts.length - 20} autres
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Toggle: Atmosphères vs Styles */}
            <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg mb-4">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-cyan-400" />
                <span className="text-white text-sm font-medium">Utiliser une atmosphère</span>
              </div>
              <Switch
                checked={useAtmosphere}
                onCheckedChange={setUseAtmosphere}
              />
            </div>

            {/* Sélecteur d'atmosphères */}
            {useAtmosphere ? (
              <div className="space-y-3">
                <Label className="text-white flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                  Atmosphère ({atmospheres.length} disponibles)
                </Label>
                {loadingAtmospheres ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                  </div>
                ) : atmospheres.length === 0 ? (
                  <div className="p-4 bg-zinc-800 rounded-lg text-center text-gray-400 text-sm">
                    Aucune atmosphère configurée.
                    <br />
                    <span className="text-xs">Utilisez les styles visuels ci-dessous.</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto pr-1">
                    {atmospheres.map(atm => (
                      <button
                        key={atm.id}
                        onClick={() => setSelectedAtmosphereId(atm.id)}
                        className={cn(
                          "p-3 rounded-lg border text-left transition-all",
                          selectedAtmosphereId === atm.id
                            ? "border-cyan-500 bg-cyan-500/20"
                            : "border-zinc-700 bg-zinc-800 hover:border-zinc-600"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{ATMOSPHERE_ICONS[atm.name] || '🎨'}</span>
                          <span className="text-white text-sm font-medium truncate">{atm.name}</span>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1 line-clamp-2">
                          {atm.base_prompt?.substring(0, 80)}...
                        </p>
                        {atm.is_default && (
                          <Badge variant="outline" className="mt-1 text-[9px] text-cyan-400 border-cyan-500/50">
                            Par défaut
                          </Badge>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Style visuel (mode classique) */
              <div className="space-y-3">
                <Label className="text-white flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-purple-400" />
                  Style visuel
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {VISUAL_STYLES.map(style => (
                    <button
                      key={style.id}
                      onClick={() => setVisualStyle(style.id)}
                      className={cn(
                        "p-3 rounded-lg border text-left transition-all",
                        visualStyle === style.id
                          ? "border-purple-500 bg-purple-500/20"
                          : "border-zinc-700 bg-zinc-800 hover:border-zinc-600"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <span>{style.icon}</span>
                        <span className="text-white text-sm font-medium">{style.name}</span>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">{style.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Format de sortie */}
            <div className="space-y-3">
              <Label className="text-white flex items-center gap-2">
                <Square className="w-4 h-4 text-green-400" />
                Format de sortie
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {ASPECT_RATIOS.map(ratio => (
                  <button
                    key={ratio.id}
                    onClick={() => setAspectRatio(ratio.id)}
                    className={cn(
                      "p-3 rounded-lg border text-center transition-all",
                      aspectRatio === ratio.id
                        ? "border-green-500 bg-green-500/20"
                        : "border-zinc-700 bg-zinc-800 hover:border-zinc-600"
                    )}
                  >
                    <ratio.icon className={cn(
                      "w-6 h-6 mx-auto mb-1",
                      aspectRatio === ratio.id ? "text-green-400" : "text-gray-400"
                    )} />
                    <p className="text-white text-sm font-medium">{ratio.name}</p>
                    <p className="text-[10px] text-gray-400">{ratio.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Nombre de variantes */}
            <div className="space-y-3">
              <Label className="text-white">Nombre de variantes par produit</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(num => (
                  <button
                    key={num}
                    onClick={() => setVariantsCount(num)}
                    className={cn(
                      "w-10 h-10 rounded-lg border font-medium transition-all",
                      variantsCount === num
                        ? "border-yellow-500 bg-yellow-500/20 text-yellow-400"
                        : "border-zinc-700 bg-zinc-800 text-gray-400 hover:border-zinc-600"
                    )}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Prompt personnalisé */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-white flex items-center gap-2">
                  ✏️ Prompt libre (optionnel)
                </Label>
                <VoiceInput 
                  onTranscript={(text) => setCustomPrompt(prev => prev + ' ' + text)}
                  className="border-zinc-700"
                />
              </div>
              <Textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Ex: Mannequin dans un décor urbain Tokyo... ou utilisez le micro 🎤"
                className="bg-zinc-800 border-zinc-700 min-h-[80px]"
              />
              <p className="text-xs text-gray-500">
                Laissez vide pour utiliser le style visuel sélectionné
              </p>
            </div>

            {/* Options */}
            <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
              <div>
                <p className="text-white text-sm">Définir comme image principale</p>
                <p className="text-xs text-gray-400">La première image générée remplacera l'image actuelle</p>
              </div>
              <Switch
                checked={setAsMain}
                onCheckedChange={setSetAsMain}
              />
            </div>

            {/* Estimation */}
            {selectedProducts.length > 0 && (
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-blue-400 text-sm">
                  📊 <strong>{selectedProducts.length * variantsCount}</strong> images seront générées
                  {estimatedTime > 60 && (
                    <span className="text-blue-300"> (~{Math.ceil(estimatedTime / 60)} min estimées)</span>
                  )}
                </p>
              </div>
            )}

            {/* Bouton de génération + Barre de progression */}
            {isGenerating ? (
              <div className="space-y-3">
                {/* Barre de progression */}
                <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white text-sm font-medium flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                      Génération en cours...
                    </span>
                    <span className="text-purple-400 font-mono text-sm">{progress}%</span>
                  </div>
                  <div className="h-3 bg-zinc-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    ⏱️ Environ {Math.ceil((selectedProducts.length * variantsCount * 20) / 60)} minute(s) restante(s)
                  </p>
                </div>
                
                {/* Bouton désactivé pendant la génération */}
                <Button
                  disabled
                  className="w-full bg-gradient-to-r from-purple-600/50 to-pink-600/50 cursor-not-allowed"
                  size="lg"
                >
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Génération de {selectedProducts.length * variantsCount} image(s)...
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleGenerate}
                disabled={selectedProducts.length === 0}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                size="lg"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Générer les visuels
              </Button>
            )}
          </div>
        ) : (
          /* Résultats */
          <div className="space-y-4 py-4">
            {/* Résumé */}
            <div className="p-4 bg-zinc-800 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-white font-medium">Génération terminée</p>
                  <p className="text-sm text-gray-400">
                    {results.summary.total_images_generated} images générées pour {results.summary.products_successful}/{results.summary.products_processed} produits
                  </p>
                </div>
                {results.summary.products_successful === results.summary.products_processed ? (
                  <Check className="w-8 h-8 text-green-400" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-yellow-400" />
                )}
              </div>
            </div>

            {/* Détails par produit */}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {results.results.map((result, idx) => (
                <div 
                  key={idx}
                  className={cn(
                    "p-3 rounded-lg border",
                    result.success 
                      ? "bg-green-500/10 border-green-500/30" 
                      : "bg-red-500/10 border-red-500/30"
                  )}
                >
                  <div className="flex items-center gap-3">
                    {result.success ? (
                      <Check className="w-5 h-5 text-green-400" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-400" />
                    )}
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium">{result.product_name}</p>
                      {result.success ? (
                        <p className="text-xs text-green-400">{result.images.length} image(s) générée(s)</p>
                      ) : (
                        <p className="text-xs text-red-400">{result.error}</p>
                      )}
                    </div>
                    {result.success && result.images.length > 0 && (
                      <div className="flex gap-1">
                        {result.images.slice(0, 3).map((img, i) => (
                          <img 
                            key={i}
                            src={getImageUrl(img)}
                            alt=""
                            className="w-10 h-10 object-cover rounded"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                onClick={handleReset}
                variant="outline"
                className="flex-1"
              >
                Générer d'autres images
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                Terminé
              </Button>
            </div>
          </div>
        )}

        {/* Barre de progression pendant la génération */}
        {isGenerating && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-zinc-900 border-t border-zinc-800">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-gray-400 mt-2 text-center">
              Génération en cours... Veuillez patienter.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AIProductImageGenerator;
